import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js";

// Connects to data-controller="reorder-table"
//
// Drag table rows to reorder them, and persist the row that moved. jQuery UI's
// sortable does the dragging, the same as every other sortable list in the app.
//
// Each row carries its own record id in data-row-id. The sortable is the
// tbody, so a row cannot move into another table. The Text tab needs that. It
// shows three of these tables at once and a rank is scoped to one slot.
export default class extends Controller {
  static values = {
    url: String
  };

  static targets = ["body"]

  connect() {
    this.sortable = $(this.bodyTarget);

    this.sortable.sortable({
      axis: "y",
      items: "> tr",
      cursor: "move",
      // Follow the pointer rather than element overlap. These rows differ in
      // height once a configuration cell wraps, and overlap tolerance will not
      // move a tall row past a short one.
      tolerance: "pointer",
      // Leave the row's own controls clickable. Remove is a submit input and
      // Edit is a link, so a drag started on either would otherwise swallow
      // the click.
      cancel: "a, button, input, select, textarea",
      // A row lifted out of the table loses the widths its cells borrowed from
      // the table and collapses. Pin each cell to the width it already had.
      helper: (event, row) => {
        row.children().each(function () {
          $(this).width($(this).width());
        });

        return row;
      },
      start: (event, ui) => ui.item.addClass("is-reordering"),
      stop: (event, ui) => {
        ui.item.removeClass("is-reordering");
        // jQuery UI restores the row's own inline styles but not the widths
        // the helper pinned onto its cells. A failed reorder keeps the row, so
        // it has to stay resizable.
        ui.item.children().css("width", "");
      },
      // Only fires when a row actually landed somewhere new. A cancelled drag
      // and a drag that ended where it started both reach stop without it.
      update: (event, ui) => this.persist(ui.item)
    });
  }

  disconnect() {
    // Every reorder replaces the whole table over Turbo Stream, so the
    // instance has to come off the tbody being discarded.
    if (this.sortable) {
      this.sortable.sortable("destroy");
      this.sortable = null;
    }
  }

  persist(row) {
    const formData = new FormData();
    formData.set('id', row.attr("data-row-id"));
    formData.set('position', row.index());

    this.element.ariaBusy = true;

    const req = new FetchRequest("patch", this.urlValue, {
      responseKind: "turbo-stream",
      body: formData,
    });

    req.perform()
      .then((response) => {
        this.element.ariaBusy = false;

        // request.js resolves for every HTTP status and rejects only when the
        // request never reached the server. So an authorization failure or a
        // server error arrives here, not in the catch below.
        //
        // A validation failure is not one of these. The controller answers
        // those with an ok turbo stream that replaces the table and corrects
        // the order.
        if (!response.ok) {
          this.#revert();
        }
      })
      .catch((error) => {
        console.error(error);
        this.element.ariaBusy = false;
        this.#revert();
      });
  }

  // The sortable is gone once a turbo stream has replaced the table, so the
  // cancel is best effort. The alert is what the reader acts on either way.
  #revert() {
    if (this.sortable) {
      this.sortable.sortable("cancel");
    }

    alert('Could not save position');
  }
}
